"use client"

import { memo } from "react"
import {
  Languages,
  Inbox,
  Headset,
  FileText,
  Calculator,
  SearchIcon,
  CreditCard,
  Percent,
  TrendingDown,
  Database,
  GitPullRequest,
  Presentation,
  Plane,
  Utensils,
  Mail,
  Briefcase,
  Lock,
  Code,
  Video,
  Cpu,
  HardDrive,
  BarChart3,
  Figma,
  Settings,
  MailWarning,
  Shield,
  type LucideIcon,
} from "lucide-react"
import "./scenario-marquee.less"

type ScenarioTag = { label: string; icon: LucideIcon; color: string }

const SCENARIO_ROWS: ScenarioTag[][] = [
  [
    { label: "多语言实时互译", icon: Languages, color: "text-emerald-400" },
    { label: "邮件自动分拣与回复", icon: Inbox, color: "text-blue-400" },
    { label: "客服工单智能应答", icon: Headset, color: "text-purple-400" },
    { label: "长文档摘要提炼", icon: FileText, color: "text-orange-400" },
    { label: "财税自动核算", icon: Calculator, color: "text-cyan-400" },
    { label: "竞品价格监测", icon: SearchIcon, color: "text-pink-400" },
  ],
  [
    { label: "保单方案生成", icon: Shield, color: "text-red-400" },
    { label: "SaaS 订阅管控", icon: CreditCard, color: "text-blue-400" },
    { label: "优惠信息聚合", icon: Percent, color: "text-yellow-400" },
    { label: "库存与价格追踪", icon: TrendingDown, color: "text-emerald-400" },
    { label: "异常数据清洗", icon: Database, color: "text-purple-400" },
    { label: "Pull Request 审查", icon: GitPullRequest, color: "text-blue-400" },
  ],
  [
    { label: "智能幻灯片生成", icon: Presentation, color: "text-blue-400" },
    { label: "差旅行程规划", icon: Plane, color: "text-cyan-400" },
    { label: "营养方案定制", icon: Utensils, color: "text-yellow-400" },
    { label: "精准客户触达", icon: Mail, color: "text-emerald-400" },
    { label: "智能人才筛选", icon: Briefcase, color: "text-purple-400" },
    { label: "会议纪要自动归档", icon: FileText, color: "text-orange-400" },
  ],
  [
    { label: "安全审计日志分析", icon: Lock, color: "text-red-400" },
    { label: "多仓库代码同步", icon: Code, color: "text-emerald-400" },
    { label: "增量自动备份", icon: Database, color: "text-blue-400" },
    { label: "批量音视频处理", icon: Video, color: "text-purple-400" },
    { label: "系统运维巡检", icon: Cpu, color: "text-cyan-400" },
    { label: "存储空间优化", icon: HardDrive, color: "text-slate-400" },
  ],
  [
    { label: "业务指标看板", icon: BarChart3, color: "text-cyan-400" },
    { label: "代码变更摘要", icon: GitPullRequest, color: "text-blue-400" },
    { label: "设计稿批量导出", icon: Figma, color: "text-orange-400" },
    { label: "系统性能调优", icon: Settings, color: "text-slate-400" },
    { label: "钓鱼邮件识别", icon: MailWarning, color: "text-red-400" },
    { label: "合规风险审查", icon: Shield, color: "text-emerald-400" },
  ],
]

const MARQUEE_DURATIONS = ["48s", "56s", "42s", "62s", "50s"]

/** Memoized marquee — never re-renders from parent state changes */
export const ScenarioMarquee = memo(function ScenarioMarquee() {
  return (
    <div className="w-full max-w-[min(96vw,1200px)] mx-auto px-4 sm:px-6 py-6">
      <div className="scenario-marquee flex flex-col gap-3">
        {SCENARIO_ROWS.map((row, ri) => {
          const dir = ri % 2 === 0 ? "left" : "right"
          return (
            <div key={ri} className="w-full overflow-hidden">
              <div
                className={`scenario-marquee__row scenario-marquee__row--${dir}`}
                style={{ "--marquee-duration": MARQUEE_DURATIONS[ri] } as React.CSSProperties}
              >
                {/* 2 copies is the minimum for seamless -50% loop (was 4 = 120 nodes, now 60) */}
                {Array.from({ length: 2 }, () => row).flat().map((tag, ti) => {
                  const Icon = tag.icon
                  return (
                    <span key={`${ri}-${ti}`} className="scenario-tag">
                      <Icon className={`h-4 w-4 ${tag.color}`} aria-hidden />
                      {tag.label}
                    </span>
                  )
                })}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
})
